import React from "react";
import { Flame, Leaf, Heart, Truck } from "lucide-react";
import useScrollReveal from "../hooks/useScrollReveal";

const FEATURES = [
  {
    icon: Flame,
    title: "Authentic Taste",
    description:
      "Every snack is crafted with traditional recipes that capture the true essence of Indian flavors.",
    gradient: "from-red-500 to-orange-500",
    bg: "bg-red-50",
    border: "border-red-100",
  },
  {
    icon: Leaf,
    title: "Quality Ingredients",
    description:
      "We source only the finest ingredients, ensuring every bite is as fresh and wholesome as possible.",
    gradient: "from-green-500 to-emerald-500",
    bg: "bg-green-50",
    border: "border-green-100",
  },
  {
    icon: Heart,
    title: "Happy Customers",
    description:
      "Over 1 million satisfied customers across India trust MunchhoNN for their daily snacking needs.",
    gradient: "from-pink-500 to-rose-500",
    bg: "bg-pink-50",
    border: "border-pink-100",
  },
  {
    icon: Truck,
    title: "Fast Delivery",
    description:
      "Pan-India distribution network ensures our fresh snacks reach you quickly, safely, and on time.",
    gradient: "from-blue-500 to-indigo-500",
    bg: "bg-blue-50",
    border: "border-blue-100",
  },
];

const Features = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-24 bg-white" data-testid="features-section">
      <div
        ref={ref}
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-sm font-semibold text-orange-500 uppercase tracking-widest">
            Why Choose Us
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mt-2">
            The{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
              MunchhoNN
            </span>{" "}
            Difference
          </h2>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {FEATURES.map(({ icon: Icon, title, description, gradient, bg, border }, idx) => (
            <div
              key={title}
              className={`group text-center p-8 rounded-3xl bg-white border ${border} shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300`}
              style={{ transitionDelay: `${idx * 60}ms` }}
              data-testid={`feature-card-${idx + 1}`}
            >
              {/* Icon container */}
              <div
                className={`w-16 h-16 ${bg} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}
              >
                <div
                  className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md`}
                >
                  <Icon size={22} className="text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">{title}</h3>
              <p className="text-stone-500 text-sm leading-relaxed">{description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
